import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarMenuButton } from '@/components/ui/sidebar';
import { Budget, cn } from '@liquidbudget/common';
import { Dialog } from '@radix-ui/react-dialog';
import { ChevronsUpDown, DropletsIcon, HomeIcon, MoonIcon, SunIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { LogoutDialog } from '../logout/logout-dialog';
import { useMenuDialog } from '../ui/use-menu-dialog';
import { BudgetSwitcherBudgets } from './budget-switcher-budgets';
import { BudgetSwitcherDashboard } from './budget-switcher-dashboard';
import { useTheme } from '@/theme';

interface Props {
  budget?: Budget;
}

export function BudgetSwitcher({ budget }: Props) {
  const { pathname } = useLocation();
  const { triggerRef, openDialog, closeDialog, dialogProps, dropdownProps } = useMenuDialog();
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <Dialog {...dialogProps}>
      <DropdownMenu {...dropdownProps}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            ref={triggerRef}
            variant="outline"
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground justify-between py-5"
          >
            <div className="flex items-center gap-1">
              <div className=" hidden sm:flex   aspect-square size-8 items-center justify-center rounded-lg ">
                <DropletsIcon className="size-5 text-sky-600" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col gap-1 leading-none pl-1 md:pl-0">
                {budget && <span className="font-semibold ">{budget.name}</span>}
                {!budget && pathname.includes('/dashboard') && <span className="font-semibold "> Dashboard</span>}
                {!budget && !pathname.includes('/dashboard') && (
                  <h1 className="text-lg font-bold mr-2">
                    <span className="text-sky-600">Liquid</span>
                    <span className="text-slate-700 dark:text-white">Budget</span>
                  </h1>
                )}
              </div>
            </div>
            <ChevronsUpDown className={cn('ml-2 h-4 w-4 shrink-0 opacity-50')} />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="ml-2 w-64 rounded-lg" align="start" side="bottom" sideOffset={4}>
          <DropdownMenuLabel className="text-xs text-muted-foreground">Liquid Budget</DropdownMenuLabel>
          <DropdownMenuItem asChild disabled={pathname === '/'}>
            <Link to="/" className="flex items-center gap-2">
              <div className="flex size-6 items-center justify-center">
                <HomeIcon className="size-4 shrink-0" />
              </div>
              Home
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={toggleTheme} className="flex items-center gap-2">
            <div className="flex size-6 items-center justify-center">
              {theme === 'dark' ? <SunIcon className="size-4 shrink-0" /> : <MoonIcon className="size-4 shrink-0" />}
            </div>
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </DropdownMenuItem>
          <BudgetSwitcherDashboard pathname={pathname} openDialog={openDialog} />
          <BudgetSwitcherBudgets budget={budget} />
        </DropdownMenuContent>
      </DropdownMenu>
      <LogoutDialog onClose={closeDialog} />
    </Dialog>
  );
}
